export interface ComparisonResult {
  missingInActual: string[];
  missingInExpected: string[];
  different: string[];
  score: number;
  details: {
    expected: any;
    actual: any;
    path: string;
  }[];
}

export class JsonComparator {
  /**
   * Deep comparison of two JSON objects with proper array handling
   */
  compareJsonData(expected: any, actual: any): ComparisonResult {
    if (!expected && !actual) {
      return {
        missingInActual: [],
        missingInExpected: [],
        different: [],
        score: 100,
        details: [],
      };
    }

    if (!expected) {
      const paths = this.getAllPaths(actual);
      return {
        missingInActual: [],
        missingInExpected: paths,
        different: [],
        score: 0,
        details: [],
      };
    }

    if (!actual) {
      const paths = this.getAllPaths(expected);
      return {
        missingInActual: paths,
        missingInExpected: [],
        different: [],
        score: 0,
        details: [],
      };
    }

    const differences: string[] = [];
    const missingInActual: string[] = [];
    const missingInExpected: string[] = [];
    const details: ComparisonResult['details'] = [];

    // Track all paths for scoring
    const allPaths = new Set<string>();

    // Compare recursively
    this.compareRecursive(expected, actual, '', differences, missingInActual, missingInExpected, details, allPaths);

    // Calculate score based on matching paths
    const totalPaths = allPaths.size;
    const errorCount = differences.length + missingInActual.length + missingInExpected.length;
    const correctPaths = Math.max(0, totalPaths - errorCount);
    const score = totalPaths > 0 ? Math.round((correctPaths / totalPaths) * 100) : 100;

    return {
      missingInActual,
      missingInExpected,
      different: differences,
      score,
      details,
    };
  }

  /**
   * Recursive comparison function
   */
  private compareRecursive(
    expected: any,
    actual: any,
    path: string,
    differences: string[],
    missingInActual: string[],
    missingInExpected: string[],
    details: ComparisonResult['details'],
    allPaths: Set<string>,
  ): void {
    // Handle null/undefined
    if (expected === null || expected === undefined) {
      if (actual !== expected) {
        differences.push(path);
        details.push({ path, expected, actual });
      }
      allPaths.add(path);
      return;
    }

    if (actual === null || actual === undefined) {
      missingInActual.push(path);
      allPaths.add(path);
      return;
    }

    // Handle arrays
    if (Array.isArray(expected)) {
      if (!Array.isArray(actual)) {
        differences.push(path);
        details.push({ path, expected, actual });
        allPaths.add(path);
        return;
      }

      // Compare array lengths
      if (expected.length !== actual.length) {
        differences.push(`${path}[length]`);
        details.push({
          path: `${path}[length]`,
          expected: expected.length,
          actual: actual.length,
        });
      }

      // Compare each element
      const maxLength = Math.max(expected.length, actual.length);
      for (let i = 0; i < maxLength; i++) {
        const elementPath = `${path}[${i}]`;

        if (i >= expected.length) {
          missingInExpected.push(elementPath);
          allPaths.add(elementPath);
        } else if (i >= actual.length) {
          missingInActual.push(elementPath);
          allPaths.add(elementPath);
        } else {
          this.compareRecursive(
            expected[i],
            actual[i],
            elementPath,
            differences,
            missingInActual,
            missingInExpected,
            details,
            allPaths,
          );
        }
      }
      return;
    }

    // Handle objects
    if (typeof expected === 'object' && expected !== null) {
      if (typeof actual !== 'object' || actual === null || Array.isArray(actual)) {
        differences.push(path);
        details.push({ path, expected, actual });
        allPaths.add(path);
        return;
      }

      // Get all keys from both objects
      const expectedKeys = Object.keys(expected);
      const actualKeys = Object.keys(actual);
      const allKeys = new Set([...expectedKeys, ...actualKeys]) as any;

      for (const key of allKeys) {
        const keyPath = path ? `${path}.${key}` : key;

        if (!(key in expected)) {
          missingInExpected.push(keyPath);
          allPaths.add(keyPath);
        } else if (!(key in actual)) {
          missingInActual.push(keyPath);
          allPaths.add(keyPath);
        } else {
          this.compareRecursive(
            expected[key],
            actual[key],
            keyPath,
            differences,
            missingInActual,
            missingInExpected,
            details,
            allPaths,
          );
        }
      }
      return;
    }

    // Handle primitive values
    allPaths.add(path);
    if (expected !== actual) {
      differences.push(path);
      details.push({ path, expected, actual });
    }
  }

  /**
   * Get all paths from an object (for initial missing checks)
   */
  private getAllPaths(obj: any, prefix = ''): string[] {
    const paths: string[] = [];

    if (obj === null || obj === undefined) {
      return prefix ? [prefix] : [];
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const arrayPath = `${prefix}[${index}]`;
        paths.push(arrayPath);
        paths.push(...this.getAllPaths(item, arrayPath));
      });
    } else if (typeof obj === 'object') {
      Object.keys(obj).forEach((key) => {
        const keyPath = prefix ? `${prefix}.${key}` : key;
        paths.push(keyPath);
        paths.push(...this.getAllPaths(obj[key], keyPath));
      });
    } else {
      if (prefix) paths.push(prefix);
    }

    return paths;
  }

  /**
   * Helper to get value by path (supports array notation)
   */
  getValue(obj: any, path: string): any {
    if (!path) return obj;

    const segments: string[] = path.match(/([^.[\]]+|\[\d+\])/g) || [];

    return segments.reduce((current: any, segment: string) => {
      if (current === null || current === undefined) return undefined;

      // Handle array index
      const arrayMatch = segment.match(/\[(\d+)\]/);
      if (arrayMatch) {
        const index = parseInt(arrayMatch[1], 10);
        return Array.isArray(current) ? current[index] : undefined;
      }

      // Handle object property
      return current[segment];
    }, obj);
  }
}
